import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Layout, Menu } from 'antd';
import { ItemType } from 'antd/es/menu/hooks/useItems';
import { MenuClickEventHandler } from 'rc-menu/lib/interface';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { HamburgerMenuIcon } from '@assets/icons/HamburgerMenu';
import { EverysetLogo } from '@assets/img';
import SaaSLogo from '@assets/img/SaaSLogo';
import { UnstyledAnchor } from '@components/UnstyledAnchor';
import { UnstyledButton } from '@components/UnstyledButton/UnstyledButton';
import { useClientEnvironment } from '@hooks/clientEnvironment';
import { useViewportWidth } from '@hooks/shared/useViewportWidth';
import Profile from '@layouts/components/Header/Profile';
import { useAuth } from '@lib/auth';
import { RootState } from '@lib/redux.lib/store';
import { EverysetUser } from '@lib/types/user.types';
import {
  getIsPublicApplicationViewFromRouterAsPath,
  removeTrailingSlashes,
} from '@lib/utils/urlHelper';

const JobsBoardLayoutContent = ({ children }: { children: ReactElement }) => {
  const { getImpersonatedUser } = useAuth();

  const loggedInUserId = useSelector(
    (state: RootState) => state.app?.user?.profile?.id
  );

  const isLoggedIn = useMemo(() => !!loggedInUserId, [loggedInUserId]);

  const [impersonatedUserData, setImpersonatedUserData] =
    useState<EverysetUser | undefined>();

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }
    const updateImperonatedUser = async () => {
      const user = await getImpersonatedUser();
      setImpersonatedUserData(user);
    };
    updateImperonatedUser();
  }, [getImpersonatedUser, isLoggedIn]);

  const impersonatedUserFullName = useMemo(() => {
    if (!impersonatedUserData) {
      return null;
    }
    return [impersonatedUserData?.first_name, impersonatedUserData?.last_name]
      .filter(Boolean)
      .join(' ');
  }, [impersonatedUserData]);

  const impersonatedUserText = useMemo(() => {
    if (!impersonatedUserFullName) {
      return null;
    }
    return `You are currently impersonating ${impersonatedUserFullName}. All
            activity will be logged.`;
  }, [impersonatedUserFullName]);

  const router = useRouter();

  const viewportWidth = useViewportWidth();
  const isMobile = useMemo(() => viewportWidth <= 768, [viewportWidth]);

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() =>
    isMobile ? true : false
  );

  const routerPathname = useMemo(() => {
    return removeTrailingSlashes(router?.pathname);
  }, [router?.pathname]);

  useEffect(() => {
    setSelectedMenuKeys(getSelectedMenuKeysFromPathname(routerPathname));
  }, [routerPathname]);

  const [selectedMenuKeys, setSelectedMenuKeys] = useState<string[]>(() => {
    return getSelectedMenuKeysFromPathname(routerPathname);
  });

  const { data: clientEnvironment } = useClientEnvironment();

  const { support_help_center_url } = clientEnvironment || {};

  const onMenuClick = useCallback<MenuClickEventHandler>(
    (e) => {
      if (!e?.key) {
        return;
      }

      if (isMobile) {
        setIsSidebarCollapsed(true);
      }

      if (e?.key === '/help-center') {
        window.open(support_help_center_url, '_blank');
        return;
      }

      setSelectedMenuKeys([e.key]);

      if (e.key === '/inbox') {
        router.push('/jobs/AvailChecks');
      } else {
        router.push(e.key);
      }
    },
    [router, isMobile, support_help_center_url]
  );

  const isPublicApplicationView = useMemo(
    () => getIsPublicApplicationViewFromRouterAsPath(router?.asPath),
    [router?.asPath]
  );

  const { logout } = useAuth();

  const isHeaderDisplayable = useMemo(() => {
    if (isMobile) {
      return (
        HEADER_MOBILE_PATHNAMES.includes(routerPathname) ||
        isPublicApplicationView
      );
    }
    return true;
  }, [routerPathname, isMobile, isPublicApplicationView]);

  const isSidebarCollapsedFinal = useMemo(() => {
    if (!isHeaderDisplayable) {
      return true;
    }
    if (!isLoggedIn) {
      return true;
    }

    return isSidebarCollapsed;
  }, [isSidebarCollapsed, isLoggedIn, isHeaderDisplayable]);

  return (
    <div>
      {isHeaderDisplayable && (
        <StyledBar
          isLoggedIn={isLoggedIn}
          isImpersonating={!!impersonatedUserData}
        >
          <div className='bar-left'>
            {isLoggedIn && (
              <StyledHamburgerMenuButton
                isImpersonating={!!impersonatedUserData}
                onClick={() => {
                  setIsSidebarCollapsed((prev) => !prev);
                }}
              >
                <HamburgerMenuIcon />
              </StyledHamburgerMenuButton>
            )}
            {isLoggedIn ? (
              <Link href='/jobs/AvailChecks' passHref>
                <StyledAnchor>
                  <LogoContent isImpersonating={!!impersonatedUserData} />
                </StyledAnchor>
              </Link>
            ) : (
              <LogoContent />
            )}
          </div>
          {!!impersonatedUserData && !!impersonatedUserText && (
            <div className='impersonated-user-text'>{impersonatedUserText}</div>
          )}

          <div className='bar-right'>
            {isLoggedIn ? (
              !isMobile && (
                <Profile impersonatedUserData={impersonatedUserData} />
              )
            ) : (
              <>
                <div className='bar-right-buttons'>
                  <Link
                    href={{
                      pathname: '/auth/login',
                      query: {
                        redirectTo: router.asPath,
                      },
                    }}
                    passHref
                  >
                    <UnstyledAnchor className='anchor-button'>
                      Login
                    </UnstyledAnchor>
                  </Link>
                  <Link href={`/auth/signup`} passHref>
                    <UnstyledAnchor className='anchor-button sign-up'>
                      Sign Up
                    </UnstyledAnchor>
                  </Link>
                </div>
              </>
            )}
          </div>
        </StyledBar>
      )}

      <StyledLayout hasSider>
        <StyledSider
          collapsedWidth={0}
          trigger={null}
          collapsible
          collapsed={isSidebarCollapsedFinal}
          width={isMobile ? 250 : 200}
        >
          <StyledMenu
            onClick={onMenuClick}
            selectedKeys={selectedMenuKeys}
            items={MENU_ITEMS}
            mode='inline'
          />
          <StyledLogoutButton onClick={() => logout()}>
            Logout
          </StyledLogoutButton>
        </StyledSider>

        <StyledLayoutContent isHeaderDisplayable={isHeaderDisplayable}>
          <StyledMask
            isSidebarCollapsed={isSidebarCollapsedFinal}
            onClick={() => {
              setIsSidebarCollapsed(true);
            }}
          />
          <div className='layout-content'>{children}</div>
        </StyledLayoutContent>
      </StyledLayout>
    </div>
  );
};

const StyledLogoutButton = styled(UnstyledButton)`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-direction: row;
    width: 100%;
    font-size: 16px;
    padding: 8px 20px;
    font-weight: 600;
    line-height: 24px;
  }
`;

type LogoContentProps = {
  isImpersonating?: boolean;
};
const LogoContent = ({ isImpersonating }: LogoContentProps) => {
  const { data: clientEnvironment } = useClientEnvironment();
  const { isEveryset } = clientEnvironment || {};

  if (isEveryset) {
    return (
      <EverysetLogo
        fill={isImpersonating ? 'white' : undefined}
        width={110}
        style={{ cursor: 'pointer' }}
      />
    );
  }

  return (
    <SaaSLogo
      style={{
        width: 110,
        height: 30,
        objectFit: 'contain',
        cursor: 'pointer',
        maxWidth: 'none',
        maxHeight: 'none',
      }}
    />
  );
};

const JobsBoardLayout = ({ children }: { children: ReactElement }) => {
  return <JobsBoardLayoutContent>{children}</JobsBoardLayoutContent>;
};

export const getJobsBoardLayout = (page: ReactElement): JSX.Element => (
  <JobsBoardLayout>{page}</JobsBoardLayout>
);

type StyledBarProps = {
  isLoggedIn: boolean;
  isImpersonating: boolean;
};
const StyledBar = styled.div<StyledBarProps>`
  background: ${({ theme, isImpersonating }) =>
    isImpersonating ? theme.palette.greyscale[7] : theme.palette.greyscale[1]};

  height: 65px;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 47;
  box-shadow: ${(props) => props.theme.palette.shadow[0]};
  padding: ${({ isLoggedIn }) => (isLoggedIn ? '0 24px 0 0' : '12px 40px')};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;

  @media (max-width: 768px) {
    padding: ${({ isLoggedIn }) => (isLoggedIn ? '0 20px 0 0' : '12px 20px')};
  }

  .bar-left {
    display: flex;
    align-items: center;
    gap: 1.63px;
  }

  .bar-right {
    display: flex;
    align-items: center;
    gap: 16px;

    @media (max-width: 768px) {
      gap: 8px;
    }

    .bar-right-buttons {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .anchor-button {
      font-size: 14px;
      line-height: 20px;
      color: ${({ theme }) => theme.palette.text[0]};
      background-color: #fff;
      padding: 5px 11px;
      border-radius: 4px;
      border: 1px solid ${({ theme }) => theme.palette.stroke[0]};
      font-weight: 600;

      &.sign-up {
        color: #fff;
        background-color: ${({ theme }) => theme.palette.primary[0]};
        border: 1px solid ${({ theme }) => theme.palette.primary[0]};
        @media (max-width: 768px) {
          display: none;
        }
      }
    }
  }

  .impersonated-user-text {
    font-size: 14px;
    color: #fff;
    width: calc(100% - 244px);
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    @media (max-width: 768px) {
      display: none; // this is a feature that comes from the casting-app, which is a desktop app
    }
  }
`;

type StyledHamburgerMenuButtonProps = {
  isImpersonating: boolean;
};
// prettier-ignore
const StyledHamburgerMenuButton = styled(UnstyledButton)<StyledHamburgerMenuButtonProps>`
  width: 65px;
  height: 65px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    path {
      fill: ${({ isImpersonating }) => (isImpersonating ? 'white' : undefined)};
    }
  }
`;

const StyledAnchor = styled(UnstyledAnchor)`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const StyledLayout = styled(Layout)`
  &.ant-layout {
    background: #fff;
    align-items: flex-start;
  }
`;

const StyledSider = styled(Layout.Sider)`
  &.ant-layout-sider {
    overflow: hidden;
    background: ${({ theme }) => theme.palette.primary[15]};
    position: sticky;
    top: 0;
    bottom: 0;
    left: 0;
    z-index: 46;
    padding-top: 65px;
    border-right: ${({ collapsed, theme }) =>
      collapsed ? 'none' : `1px solid ${theme.palette.stroke[0]}`};

    min-height: 100dvh;

    @media (max-width: 768px) {
      position: fixed;
      border-right: none;
    }

    .ant-layout-sider-children {
      padding: 20px;
      display: flex;
      flex-direction: column;
      justify-content: space-between;

      @media (max-width: 768px) {
        padding: 32px 20px;
      }
    }
  }
`;

type StyledLayoutContentProps = {
  isHeaderDisplayable: boolean;
};
const StyledLayoutContent = styled(Layout.Content)<StyledLayoutContentProps>`
  &.ant-layout-content {
    padding-top: ${({ isHeaderDisplayable }) =>
      isHeaderDisplayable ? '65px' : '0px'};
  }
`;

type StyledMaskProps = {
  isSidebarCollapsed: boolean;
};
const StyledMask = styled.div<StyledMaskProps>`
  position: fixed;
  top: 0px;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 45;
  background: rgba(0, 0, 0, 0.45);
  display: none;
  @media (max-width: 768px) {
    display: ${({ isSidebarCollapsed }) =>
      isSidebarCollapsed ? 'none' : 'block'};
  }
`;

const StyledMenu = styled(Menu)`
  &.ant-menu-light.ant-menu-root.ant-menu-inline {
    border: none;
    background-color: transparent;

    display: flex;
    flex-direction: column;
    gap: 8px;

    ::before {
      display: none;
    }

    ::after {
      display: none;
    }

    .ant-menu-item {
      margin: 0;
      color: #000000;
      height: auto;

      font-size: 14px;
      line-height: 20px;
      padding: 8px 16px !important;
      width: 100%;

      @media (max-width: 768px) {
        padding: 10px 16px;
        line-height: 24px;
        padding: 10px 20px !important;
        font-size: 16px;
      }

      &.ant-menu-item-selected {
        background-color: ${({ theme }) => theme.palette.stroke[6]};
        font-weight: 600;
        color: ${({ theme }) => theme.palette.primary[0]};
      }
    }
  }
`;

const MENU_ITEMS: ItemType[] = [
  {
    label: 'Inbox',
    key: '/inbox',
  },
  {
    label: 'Job Board',
    key: '/job-board',
  },
  {
    label: 'My Profile',
    key: '/profile',
  },
  {
    label: 'My Account',
    key: '/Accounts/MyAccount',
  },
  {
    label: 'Help Center',
    key: '/help-center',
  },
].map((item) => ({ ...item, title: null }));

const getSelectedMenuKeysFromPathname = (pathname: string) => {
  if (!pathname) {
    return [];
  }

  if (
    pathname === '' ||
    pathname === '/' ||
    INBOX_PATHNAMES.includes(pathname)
  ) {
    return ['/inbox'];
  }

  return [pathname];
};

const INBOX_PATHNAMES = [
  '/jobs/AvailChecks',
  '/jobs/StatusChecks',
  '/jobs/Bookings',
];

const HEADER_MOBILE_PATHNAMES = [
  '/jobs/AvailChecks',
  '/jobs/Bookings',
  '/jobs/StatusChecks',
  '/profile',

  '/Accounts/MyAccount',
  '/Accounts/ManageAccount',
  '/job-board',
  '/apps/[application_id]',
];
